import { useState, useEffect, useRef } from 'react';
import type { FC } from 'react';

import {
  EntityPagesCommbox,
  ATTACHMENT_TYPES,
  COMMBOX_ACTOR_TYPES,
} from '@xing-com/crate-commbox-entity-pages-commbox';
import { usePageContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-entity-page-context';

import { COMMBOX_SELECTABLE_AUDIENCES } from '../../config/commbox';
import {
  trackCommboxAudienceSelect,
  trackCommboxOpenActorSwitch,
  trackCommboxOpenAudienceSelection,
} from '../../tracking';
import { mapAudienceTrackingValue } from '../../utils/audience';

const COMMBOX_SUCESS_DIALOG_VISIBILITY_TIMEOUT = 7000;

type CreatePostButtonProps = {
  children: (onClick: () => void) => React.JSX.Element;
  onCompleted?: () => void;
  page: 'entity_pages/overview' | 'entity_pages/subpage/news';
};

export const CreatePostButton: FC<CreatePostButtonProps> = ({
  page,
  onCompleted = () => undefined,
  children,
}) => {
  const { pageContext } = usePageContext();

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [postWasSent, setPostWasSent] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successPostHeadLine, setSuccessPostHeadLine] = useState(
    'COMMBOX_SUCCESS_HEADLINE'
  );
  const [successPostHeadLineValues, setSuccessPostHeadLineValues] = useState(
    {}
  );
  const [successPostDescription, setSuccessPostDescription] = useState(
    'COMMBOX_SUCCESS_DESC'
  );
  const [successPostDescriptionValues, setSuccessPostDescriptionValues] =
    useState({});

  const setBannerWhenScheduling = (publishAt?: string | null) => {
    let successMessageValues = {};
    let successMessage = 'COMMBOX_SUCCESS_DESC';
    if (publishAt) {
      setSuccessPostHeadLine('COMMBOX_SUCCESS_HEADLINE');
      setSuccessPostHeadLineValues({});
      successMessage = 'COMMBOX_SUCCESS_SCHEDULED_DESC';
      const publishedDate = new Date(publishAt);
      successMessageValues = {
        date: `${publishedDate.getDate()}/${
          publishedDate.getMonth() + 1
        }/${publishedDate.getFullYear()}`,
        time: `${publishedDate.getHours()}:${
          publishedDate.getMinutes() < 10
            ? `0${publishedDate.getMinutes()}`
            : publishedDate.getMinutes()
        }`,
      };
      setSuccessPostDescription(successMessage);
      setSuccessPostDescriptionValues(successMessageValues);
    }
  };

  useEffect(() => {
    if (postWasSent) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      timeoutRef.current = setTimeout(() => {
        setShowSuccessMessage(false);
        setSuccessPostDescription('COMMBOX_SUCCESS_DESC');
        setSuccessPostDescriptionValues({});
        timeoutRef.current = null;
      }, COMMBOX_SUCESS_DIALOG_VISIBILITY_TIMEOUT);
      setPostWasSent(false);
    }
  }, [postWasSent]);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  return (
    <EntityPagesCommbox
      application="ENTITY_PAGES"
      globalId={pageContext.globalId}
      pageId={pageContext.globalId}
      pageName={page}
      actors={[
        {
          logo: pageContext.logo ?? '',
          actorName: pageContext.pageTitle,
          globalId: pageContext.globalId,
          type: COMMBOX_ACTOR_TYPES.PAGE,
          // @ts-expect-error TODO: fix this type
          features: [ATTACHMENT_TYPES.IMAGE, ATTACHMENT_TYPES.VIDEO],
        },
      ]}
      selectableAudiences={COMMBOX_SELECTABLE_AUDIENCES}
      successMessage={{
        description: successPostDescription,
        descriptionValues: successPostDescriptionValues,
        headline: successPostHeadLine,
        headlineValues: successPostHeadLineValues,
        setShowSuccessMessage,
        showSuccessMessage,
      }}
      featurePermissions={{
        enableMentions: true,
        enableVideo: true,
        enableScheduling: pageContext.isPaidPage,
        enablePolls: false,
        enableMultiImage: false,
        enableSchedulingLock: pageContext.isCompanyPage,
      }}
      tagManagerId="btn_postnews"
      onOpenAudienceSelection={() => trackCommboxOpenAudienceSelection()}
      onOpenActorSwitch={() => trackCommboxOpenActorSwitch()}
      onSelectAudience={(id) => {
        if (id === 'PUBLIC' || id === 'PRIVATE') {
          trackCommboxAudienceSelect(mapAudienceTrackingValue(id));
        }
      }}
      onCreateStarted={({ publishAt }) => {
        setBannerWhenScheduling(publishAt);
      }}
      onCreateCompleted={() => {
        setShowSuccessMessage(true);
        setPostWasSent(true);
        onCompleted();
      }}
    >
      {children}
    </EntityPagesCommbox>
  );
};
